/*
 * @Author: Chao Yang
 * @Date: 2019-12-12 03:32:01
 * @LastEditor: Chao Yang
 * @LastEditTime: 2022-09-24 16:14:20
 */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as Document from 'src/app/domain/model/document';
import { AccountProxy } from 'src/app/domain/proxy';
import * as IDtoAccountGroup from 'src/app/domain/model/account-group/index';
import { RootService } from 'src/app/domain/service/root/index';
import { DatePipe } from '@angular/common';
import { InfoService } from '../info/info.service';

@Injectable()
export class AccountService {
  constructor(
    private _accountProxy: AccountProxy,
    private rootService: RootService,
    private datePipe: DatePipe,
    private infoService: InfoService
  ) { }

  getAccounts(
    fundIds: number[],
    accounts2: IDtoAccountGroup.IAccountItem[]
  ): Observable<Document.IAccount[]> {
    return this._accountProxy.getAccounts(fundIds, accounts2).pipe(
      map((accounts) => {
        accounts.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
        return accounts;
      })
    );
  }

  GetReportAccountDetail(
    programId: number,
    instanceId: number,
    accountId: number,
    start: Date,
    end: Date
  ) {
    return this._accountProxy.GetReportAccountDetail(
      programId,
      instanceId,
      accountId,
      start,
      end,
      this.rootService.viewDataForAllInvestors
    );
  }

  GetReportAccountSummary(programId: number, accountId: number) {
    return this._accountProxy.GetReportAccountSummary(
      programId,
      accountId,
      this.rootService.viewDataForAllInvestors
    );
  }

  GetReportAccountSummary2023(
    programId: number,
    accountId: number,
    start: Date,
    end: Date
  ) {
    return this._accountProxy.GetReportAccountSummary2023(
      programId,
      accountId,
      this.rootService.viewDataForAllInvestors,
      start,
      end
    );
  }

  GetReportUserProgramDateRange(
    programId: number,
    instanceId: number,
    accountId: number,
    start: Date,
    end: Date
  ) {
    if (start == null || start == undefined) {
      start = new Date('2000/01/01');
    }
    return this._accountProxy.GetReportUserProgramDateRange(
      programId,
      instanceId,
      accountId,
      start,
      end
    );
  }

  getAccountGroups(): Observable<IDtoAccountGroup.IItem[]> {
    return this._accountProxy.getAccountGroups();
  }

  GetReportUserProgramITDEnd(
    programId: number,
    instanceId: number,
    accountId: number
  ) {
    return this._accountProxy.GetReportUserProgramITDEnd(
      programId,
      instanceId,
      accountId
    );
  }

  getReportUserProgramDateRange(
    programId: number,
    instanceId: number,
    accountId: number
  ) {
    return this._accountProxy.getReportUserProgramDateRange(
      programId,
      instanceId,
      accountId
    );
  }

  getTransactions(conditions: any[]): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
    }[]
  > {
    const timeZone = this.infoService.getSiteTimeZone();
    return this._accountProxy.getTransactions(conditions).pipe(
      map((transactions) => {
        transactions.forEach((transaction) => {
          if (transaction.transactionType === null) {
            transaction.transactionType = '';
          }
          transaction.effectiveDate = new Date(
            this.datePipe.transform(
              transaction.effectiveDate,
              'yyyy/MM/dd',
              timeZone
            )
          ).getTime();
        });
        return transactions;
      })
    );
  }

  getBalanceTrans(conditions: any[]): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
    }[]
  > {
    const timeZone = this.infoService.getSiteTimeZone();
    return this._accountProxy.getBalanceTrans(conditions).pipe(
      map((transactions) => {
        transactions.forEach((transaction) => {
          transaction.effectiveDate = new Date(
            this.datePipe.transform(
              transaction.effectiveDate,
              'yyyy/MM/dd',
              timeZone
            )
          ).getTime();
        });
        return transactions;
      })
    );
  }

  getPNLTransactions(conditions: any[]): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
    }[]
  > {
    const timeZone = this.infoService.getSiteTimeZone();
    return this._accountProxy.getPNLTransactions(conditions).pipe(
      map((transactions) => {
        transactions.forEach((transaction) => {
          transaction.effectiveDate = new Date(
            this.datePipe.transform(
              transaction.effectiveDate,
              'yyyy/MM/dd',
              timeZone
            )
          ).getTime();
        });
        return transactions;
      })
    );
  }

  getOutstandingCommitment(conditions: any[]): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
      percentage: number;
    }[]
  > {
    const timeZone = this.infoService.getSiteTimeZone();
    return this._accountProxy.getOutstandingCommitment(conditions).pipe(
      map((transactions) => {
        transactions.forEach((transaction) => {
          transaction.effectiveDate = new Date(
            this.datePipe.transform(
              transaction.effectiveDate,
              'yyyy/MM/dd',
              timeZone
            )
          ).getTime();
        });
        return transactions;
      })
    );
  }

  getTransactions2023(conditions: any[], displayClosedAccounts: boolean): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
    }[]
  > {
    const timeZone = this.infoService.getSiteTimeZone();
    return this._accountProxy.getTransactions2023(conditions, displayClosedAccounts).pipe(
      map((transactions) => {
        transactions.forEach((transaction) => {
          if (transaction.transactionType === null) {
            transaction.transactionType = '';
          }
          transaction.effectiveDate = new Date(
            this.datePipe.transform(
              transaction.effectiveDate,
              'yyyy/MM/dd',
              timeZone
            )
          ).getTime();
        });
        return transactions;
      })
    );
  }

  getBalanceTrans2023(conditions: any[], displayClosedAccounts: boolean): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
    }[]
  > {
    const timeZone = this.infoService.getSiteTimeZone();
    return this._accountProxy.getBalanceTrans2023(conditions, displayClosedAccounts).pipe(
      map((transactions) => {
        transactions.forEach((transaction) => {
          transaction.effectiveDate = new Date(
            this.datePipe.transform(
              transaction.effectiveDate,
              'yyyy/MM/dd',
              timeZone
            )
          ).getTime();
        });
        return transactions;
      })
    );
  }

  getPNLTransactions2023(conditions: any[], displayClosedAccounts: boolean): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
    }[]
  > {
    const timeZone = this.infoService.getSiteTimeZone();
    return this._accountProxy.getPNLTransactions2023(conditions, displayClosedAccounts).pipe(
      map((transactions) => {
        transactions.forEach((transaction) => {
          transaction.effectiveDate = new Date(
            this.datePipe.transform(
              transaction.effectiveDate,
              'yyyy/MM/dd',
              timeZone
            )
          ).getTime();
        });
        return transactions;
      })
    );
  }

  getOutstandingCommitment2023(conditions: any[], displayClosedAccounts: boolean): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
      percentage: number;
    }[]
  > {
    const timeZone = this.infoService.getSiteTimeZone();
    return this._accountProxy.getOutstandingCommitment2023(conditions, displayClosedAccounts).pipe(
      map((transactions) => {
        transactions.forEach((transaction) => {
          transaction.effectiveDate = new Date(
            this.datePipe.transform(
              transaction.effectiveDate,
              'yyyy/MM/dd',
              timeZone
            )
          ).getTime();
        });
        return transactions;
      })
    );
  }
}
